import { useContext, useEffect } from 'react'

import { ComposerContext } from '../ComposerContext'
import { registerChannelMirrorFlush } from './channelMirrorFlush'
import { installFluxToChannelVariantsMirror } from './installFluxToChannelVariantsMirror'

/**
 * Mount the Flux→Zustand per-channel mirror.
 *
 * `channelBridgeReady` flips true only after install (which reconciles the
 * collection synchronously) and false first on teardown, so a reader never sees
 * a half-seeded collection. Must run inside `ComposerContext.Provider`.
 *
 * @see documentation/work-notes/2026-08-04-composer-flux-to-zustand-bridge.md
 */
export function useChannelBridge(): void {
  const store = useContext(ComposerContext)
  if (!store) {
    throw new Error('useChannelBridge must be used within ComposerProvider')
  }

  useEffect(() => {
    const mirror = installFluxToChannelVariantsMirror(store)
    store.getState().setChannelBridgeReady(true)
    const unregisterMirrorFlush = registerChannelMirrorFlush(mirror.flush)

    return (): void => {
      store.getState().setChannelBridgeReady(false)
      unregisterMirrorFlush()
      mirror.unsubscribe()
    }
  }, [store])
}
